import { z } from 'zod';
import { createSchema } from 'zod-openapi';
import { CountryCodeSchema } from './candidate.dto';
import {
	AVAILABILITY,
	DRIVING_LICENCE,
	GCC_EXPERIENCE,
	LANGUAGE_LEVELS,
	MANAGEMENT_EXPERIENCE,
	SALARY_EXPECTATIONS,
	VISA_OPTIONS,
} from './merit.catalogs';
import { PHONE_PATTERN } from './settings.dto';

// ============================================================================
// M02 · Candidate profile + 10-step onboarding (public URL)
//
// Shared contract between the public/authenticated wizard and the API. The
// wizard is ONE component used in both modes (M02 §5), so both talk this shape.
//
// Catalog-backed fields travel as the catalog LABEL (string), the same way the
// rest of Merit stores them — the master catalogs live in `catalog_items` and
// their values are editable, so an enum here would reject anything the admin
// adds (see the enum→string fix in candidate.dto.ts).
// ============================================================================

/** Lifecycle of a candidate profile (M02 §2). */
export const ONBOARDING_STATUSES = [
	'draft', // borrador — being filled in
	'pending_verification', // pendiente_verificar — submitted, internal scoring only
	'in_validation', // en_validacion — the team is reviewing it
	'verified', // verificado — login enabled, has access
	'rejected', // rechazado
] as const;

export type OnboardingStatus = (typeof ONBOARDING_STATUSES)[number];

/** How the candidate record came to be (M02 §2). */
export const ONBOARDING_SOURCES = ['manual_intake', 'public_url'] as const;

/** The ten steps, in the exact order of the prototype's CAND_STEPS (M02 §3). */
/**
 * Orden del asistente. **El CV va primero** desde
 * MD-Onboarding-Candidato-CV-IA §2: se sube, la IA lo lee y el resto de pasos
 * llegan prerrellenados en vez de en blanco. Antes era el paso 9, que es tanto
 * como pedirle a la persona que teclee lo que ya trae escrito.
 *
 * El orden importa: `OnboardingProgress.currentStep` es un índice sobre este
 * array, así que reordenarlo mueve dónde retoma quien dejó el perfil a medias.
 */
// CV y foto primero (clienta, 03/09 · 36): el CV se lee y rellena el resto;
// la persona revisa y completa después.
export const ONBOARDING_STEPS = [
	'cv',
	'photo',
	'personal',
	'experience',
	'education',
	'languages',
	'legal',
	'availability',
	'preferences',
	'video',
] as const;

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

/**
 * What the wizard SHOWS (23/09, client): the three files together in one
 * «Documents» block at the top, then the seven data steps. It is a grouping on
 * screen only — `ONBOARDING_STEPS` stays as it is, because `currentStep` is an
 * index over it and candidates in production are halfway through the wizard.
 */
export const ONBOARDING_SCREENS = [
	'documents',
	'personal',
	'experience',
	'education',
	'languages',
	'legal',
	'availability',
	'preferences',
] as const;

export type OnboardingScreen = (typeof ONBOARDING_SCREENS)[number];

/** The storage steps each screen owns. */
export const ONBOARDING_SCREEN_STEPS: Record<OnboardingScreen, readonly OnboardingStep[]> = {
	documents: ['cv', 'photo', 'video'],
	personal: ['personal'],
	experience: ['experience'],
	education: ['education'],
	languages: ['languages'],
	legal: ['legal'],
	availability: ['availability'],
	preferences: ['preferences'],
};

/** The screen that shows a storage step. */
export function screenOfStep(step: OnboardingStep): OnboardingScreen {
	const found = ONBOARDING_SCREENS.find((screen) => ONBOARDING_SCREEN_STEPS[screen].includes(step));
	return found ?? 'documents';
}

/** Language proficiency scale of M02 §2 (distinct from the CEFR one of intake). */
export const PROFICIENCY_LEVELS = ['basic', 'intermediate', 'fluent', 'native'] as const;

/** Education level. */
export const EDUCATION_LEVELS = ['secondary', 'vocational', 'bachelor', 'master', 'doctorate', 'other'] as const;

// ---- File limits (M02 §2, §3) ----------------------------------------------

/**
 * CV: PDF, DOC o DOCX, 5 MB (MD-Onboarding-Candidato-CV-IA §4).
 *
 * Los tres los pide la clienta. El tipo declarado lo pone el navegador y no
 * siempre acierta —Windows manda `.docx` como `application/octet-stream` más a
 * menudo de lo que parece—, así que la lista es amplia y **quien decide de
 * verdad es la firma del fichero** (`assertCvSignature`), que el cliente no
 * controla.
 */
export const CV_MAX_BYTES = 5 * 1024 * 1024;
export const CV_MIME_TYPES = [
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/octet-stream',
] as const;

/** Lo que se le enseña a la persona en la zona de subida. */
export const CV_EXTENSIONS = ['.pdf', '.doc', '.docx'] as const;

/**
 * Presentation video: mandatory, 30–90 s.
 *
 * **200 MB desde el 25/08** (documento de altas). Estaba en 50 MB porque ese es
 * el tope por fichero del plan gratuito de Supabase Storage, y prometer más
 * dejaba elegir un fichero que moría a mitad de subida en vez de avisar antes.
 * Sigue siendo verdad al revés: este número **no** manda solo. Antes de que un
 * vídeo de 200 MB llegue a guardarse tiene que pasar por tres topes más, y dos
 * NO están en este repositorio:
 *
 *  1. `INTAKE_UPLOAD_LIMITS.fileSize` (multer) — sale de esta constante. ✔
 *  2. `MAX_UPLOAD_MB` — variable de entorno; los `.env.example` la traen a 200,
 *     pero en staging/prod la pone Dokploy a mano. Vacía = sin tope de app.
 *  3. **El límite del bucket de Supabase Storage** — por proyecto/plan, se
 *     cambia en el panel de Supabase, no aquí.
 *  4. **El proxy de delante** (Traefik/Dokploy). Si recorta el cuerpo, la
 *     petición muere con un 413 que la aplicación nunca ve.
 *
 * Subir este número sin los otros tres solo cambia dónde falla la subida.
 */
export const VIDEO_MIN_SECONDS = 30;
export const VIDEO_MAX_SECONDS = 90;

/** Error codes of the 30–90 s rule (`assertPresentationVideoDuration`); the out-of-range one ends in `:<seconds>`. */
export const VIDEO_DURATION_UNREADABLE = 'VIDEO_DURATION_UNREADABLE';
export const VIDEO_DURATION_OUT_OF_RANGE = 'VIDEO_DURATION_OUT_OF_RANGE';
export const VIDEO_MAX_BYTES = 200 * 1024 * 1024;
export const VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'] as const;

/** Profile photo: one frontal shot on a neutral background. */
export const PHOTO_MAX_BYTES = 8 * 1024 * 1024;
export const PHOTO_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/**
 * Licencia comercial y certificado VAT del alta de empresa: un PDF exportado o
 * el escaneo de uno.
 *
 * Estaba solo en `backend/src/common/utils/intake-upload.ts`, así que el
 * formulario no lo conocía y avisaba con el tope general (`MAX_UPLOAD_MB`).
 * Resultado: se podía elegir un fichero de 40 MB, esperar la subida entera y
 * recibir un 413 diciendo "máx. 10 MB". Vive aquí para que los dos lados
 * respondan lo mismo antes de gastar la subida.
 */
export const COMPANY_DOC_MAX_BYTES = 10 * 1024 * 1024;

// ============================================================================
// Step payloads — one schema per step, so `PATCH /onboarding/:token/step/:step`
// validates exactly what that step owns and nothing else.
// ============================================================================

/** 1 · Datos personales. */
export const PersonalStepSchema = z.object({
	fullName: z.string().trim().min(2).max(160),
	// Texto libre histórico (etiqueta del catálogo `nationalities`, que son
	// nombres de país en español). Ya no se elige a mano: lo deriva `countryCode`.
	// Se conserva porque es lo que declaró la persona — ver `CandidateLead.countryCode`.
	nationality: z.string().trim().min(1), // catalog: nationalities
	/**
	 * País de nacionalidad en ISO alfa-2. Opcional en el contrato porque los
	 * borradores anteriores a la cascada solo tienen el texto libre y tienen que
	 * poder seguir guardando; la pantalla sí lo exige.
	 */
	countryCode: CountryCodeSchema.nullable().optional(),
	birthDate: z.iso.date(),
	city: z.string().trim().min(1), // catalog: cities
	phone: z.string().trim().min(5).max(40).regex(PHONE_PATTERN, 'INVALID_PHONE'),
	email: z.email(),
	/**
	 * Required since 23/09: the wizard is now the public signup, and the short
	 * form it replaces already asked for it.
	 */
	linkedinUrl: z.url(),
	/**
	 * Altura en cm (Jumeirah 13-ago). **Obligatoria desde el 02/09** (C-P1-10:
	 * «Required en TODOS los formularios de candidato») pero sigue siendo solo
	 * informativa: se enseña y como mucho ordena — la apariencia física es
	 * criterio prohibido en el motor de matching y este campo no es la excepción.
	 */
	heightCm: z.number().int().min(100).max(250),
});

/** 3 · Experiencia laboral (N entradas). `toDate` null = current job. */
export const ExperienceEntrySchema = z
	.object({
		position: z.string().trim().min(1), // catalog: positions
		department: z.string().trim().min(1), // catalog: departments
		employerProperty: z.string().trim().min(1).max(200),
		luxuryLevel: z.string().trim().min(1), // catalog: luxury_levels
		/**
		 * País de ESE empleo. Opcional en el contrato para que un borrador o un
		 * CV a medias sigan parseando; el asistente lo exige al guardar.
		 */
		countryCode: CountryCodeSchema.nullable().optional(),
		fromDate: z.iso.date(),
		toDate: z.iso.date().optional().nullable(),
		description: z.string().trim().max(2000).optional().nullable(),
	})
	.refine((e) => !e.toDate || e.toDate >= e.fromDate, {
		message: 'toDate must not be earlier than fromDate',
		path: ['toDate'],
	});

/**
 * At least one job since 13/09 (client) — the submit already refused none; the
 * step accepts none no more either (23/09), so the form and the server agree.
 *
 * `managementExperience` lives here because it is about the career, and the
 * short signup form that asked for it is gone (23/09). It is scored, so it is
 * mandatory. Stored only on the lead: there is no step table column for it.
 */
export const ExperienceStepSchema = z.object({
	entries: z.array(ExperienceEntrySchema).min(1).max(20),
	managementExperience: z.enum(MANAGEMENT_EXPERIENCE),
});

/** 4 · Formación y certificaciones. */
export const EducationEntrySchema = z.object({
	level: z.enum(EDUCATION_LEVELS),
	title: z.string().trim().min(1).max(200),
	institution: z.string().trim().min(1).max(200),
	year: z.number().int().min(1950).max(2100),
	isCertification: z.boolean().default(false),
});

/** Mandatory for everybody (client, 23/09): at least one degree or certificate. */
export const EducationStepSchema = z.object({
	entries: z.array(EducationEntrySchema).min(1).max(20),
});

/**
 * 5 · Idiomas. `percent` is the self-assessed command shown as a bar.
 *
 * `cefr` (23/09): the level the person picks. The four-word scale folds B1/B2
 * together, and the lead goes back to CEFR on save taking the LOWER of the
 * pair — so a B2 speaker who chose «intermediate» was stored as B1 and tripped
 * the «English below B2» critical fail: an automatic rejection. That only hit
 * the odd migrated candidate while the signup form asked CEFR directly; with
 * the wizard as the signup it would hit everybody. `level` stays (the step
 * table and every screen read it) and is derived from `cefr` when it comes.
 */
export const LanguageEntrySchema = z.object({
	language: z.string().trim().min(1), // catalog: languages
	level: z.enum(PROFICIENCY_LEVELS),
	cefr: z.enum(LANGUAGE_LEVELS).optional().nullable(),
	percent: z.number().int().min(0).max(100).optional().nullable(),
});

export const LanguagesStepSchema = z.object({
	entries: z.array(LanguageEntrySchema).min(1).max(15),
});

/** 6 · Situación legal. */
export const LegalStepSchema = z.object({
	visaStatus: z.enum(VISA_OPTIONS),
	emiratesId: z.string().trim().max(40).optional().nullable(),
	needsSponsor: z.boolean(),
	currentEmployment: z.string().trim().max(200).optional().nullable(),
	/** Scored; asked here since the short signup form is gone (23/09). Lives on the lead. */
	drivingLicence: z.enum(DRIVING_LICENCE),
});

/** 7 · Disponibilidad. */
export const AvailabilityStepSchema = z.object({
	availability: z.enum(AVAILABILITY),
	relocation: z.string().trim().min(1),
	/**
	 * Where they want to work (13/09, client): the same «ubicaciones de
	 * interés» the signup form asks for. The wizard never collected it, so a
	 * migrated candidate could finish with none and the record read as
	 * incomplete. Mirrored onto the lead, which is what matching reads.
	 */
	locationTargeting: z.array(z.string().trim().min(1)).min(1).max(10), // catalog: cities
	gccExperience: z.enum(GCC_EXPERIENCE),
	/**
	 * The salary BRACKET (23/09): the unit the scoring and the vacancy matching
	 * compare, and what the short signup form asked. Lives on the lead. The
	 * free AED number below stays readable for old rows; the screen no longer
	 * asks it, because two salary questions in one step read as a trick.
	 */
	salaryExpectation: z.enum(SALARY_EXPECTATIONS),
	salaryExpectationAed: z.number().int().min(0).optional().nullable(),
	/** Days of notice at the current job (MD-Vacante §5.5) — a matching input. */
	noticePeriodDays: z.number().int().min(0).max(365).optional().nullable(),
});

/** 8 · Preferencias profesionales. Up to 3 target positions (M02 §2). */
export const PreferencesStepSchema = z.object({
	// «Otro» vuelve (clienta, 03/09 · 31; había salido el 02/09): un puesto fuera
	// del catálogo se escribe en `targetPositionOther` y Kairos lo normaliza
	// desde el panel. Hasta entonces no cruza con ninguna vacante por puesto.
	targetPositions: z.array(z.string().trim().min(1)).min(1).max(3), // catalog: positions
	/** El puesto en sus palabras, obligatorio si eligió «Other». */
	targetPositionOther: z.string().trim().max(120).optional().nullable(),
	propertyTypes: z.array(z.string().trim().min(1)).max(10), // catalog: property_types
	/** Years IN the target role (MD-Vacante §6) — more predictive than total. */
	yearsInTargetRole: z.number().int().min(0).max(60).optional().nullable(),
	/** Systems/tools operated (SYSTEMS catalog + Other) — a matching input. */
	systems: z.array(z.string().trim().min(1)).max(15).optional(),
	/** Specializations (matrix §4, SPECIALIZATIONS catalog) — a matching input. */
	specializations: z.array(z.string().trim().min(1)).max(15).optional(),
	/**
	 * Habilidades en texto libre (25/08 · fase 5 §6.3). Deliberadamente separadas
	 * de `systems` y `specializations`: aquellas dos tienen catálogo y entran en
	 * el matching; estas las escribe la persona y solo se muestran.
	 */
	skills: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
});
// «Other» sin texto se rechaza en el servidor (`savePreferences`) y en el asistente: un
// `refine` aquí rompería `.partial()`, que usa el estado del asistente.

/**
 * Steps 2 (photo), 9 (CV) and 10 (video) carry no JSON body: the file is
 * uploaded to its own endpoint, which stores it and marks the step complete.
 */
export const UploadStepAckSchema = z.object({});

/** Per-step payload map, so the service can validate `:step` against its shape. */
export const ONBOARDING_STEP_SCHEMAS = {
	personal: PersonalStepSchema,
	photo: UploadStepAckSchema,
	experience: ExperienceStepSchema,
	education: EducationStepSchema,
	languages: LanguagesStepSchema,
	legal: LegalStepSchema,
	availability: AvailabilityStepSchema,
	preferences: PreferencesStepSchema,
	cv: UploadStepAckSchema,
	video: UploadStepAckSchema,
} as const;

export type PersonalStepDTO = z.infer<typeof PersonalStepSchema>;
export type ExperienceStepDTO = z.infer<typeof ExperienceStepSchema>;
export type EducationStepDTO = z.infer<typeof EducationStepSchema>;
export type LanguagesStepDTO = z.infer<typeof LanguagesStepSchema>;
export type LegalStepDTO = z.infer<typeof LegalStepSchema>;
export type AvailabilityStepDTO = z.infer<typeof AvailabilityStepSchema>;
export type PreferencesStepDTO = z.infer<typeof PreferencesStepSchema>;

// ============================================================================
// Responses
// ============================================================================

/** A stored file as the wizard needs to render it (name, size, preview). */
export const OnboardingFileSchema = z.object({
	id: z.number().int(),
	originalName: z.string(),
	mimeType: z.string().nullable(),
	fileSize: z.number().int().nullable(),
	publicUrl: z.string(),
	durationSeconds: z.number().int().nullable().optional(),
});

/**
 * What `GET /onboarding/:token` returns: everything the wizard needs to render
 * and resume, and NOTHING about the internal score — the candidate must never
 * see it (M02 §4, security).
 */
export const OnboardingStateSchema = z.object({
	candidateId: z.number().int(),
	status: z.enum(ONBOARDING_STATUSES),
	/** Step the candidate left off on, 0-based over ONBOARDING_STEPS. */
	currentStep: z
		.number()
		.int()
		.min(0)
		.max(ONBOARDING_STEPS.length - 1),
	/** Steps already saved and valid, so the wizard can mark them done. */
	completedSteps: z.array(z.enum(ONBOARDING_STEPS)),
	/** Whether every mandatory step is satisfied — drives the submit button. */
	canSubmit: z.boolean(),
	/** Reasons submit is blocked, for the review screen (i18n keys). */
	blockingReasons: z.array(z.string()),
	personal: PersonalStepSchema.partial().nullable(),
	experience: z.array(ExperienceEntrySchema),
	/** Saved with the experience step, read from the lead. Empty string = never answered. */
	managementExperience: z.string().nullable(),
	education: z.array(EducationEntrySchema),
	languages: z.array(LanguageEntrySchema),
	legal: LegalStepSchema.partial().nullable(),
	availability: AvailabilityStepSchema.partial().nullable(),
	preferences: PreferencesStepSchema.partial().nullable(),
	photo: OnboardingFileSchema.nullable(),
	cv: OnboardingFileSchema.nullable(),
	video: OnboardingFileSchema.nullable(),
	/** Token expiry, so the UI can warn before it dies. */
	expiresAt: z.iso.datetime().nullable(),
	/**
	 * The latest CV reading that proposed something (23/09). The proposal lives
	 * only in the browser until each step is saved, so a reload — or coming back
	 * days later with the link — lost it for every step not yet saved. With the
	 * id the wizard re-applies it on open, to the unsaved steps only.
	 */
	cvExtractionId: z.number().int().nullable().optional(),
	/**
	 * A migrated candidate whose changes to the imported data are with the team
	 * (4.1, 11/09): the wizard shows "we are reviewing your details" instead of
	 * the steps.
	 */
	underReview: z.boolean(),
	/**
	 * What the team asked this candidate to fix (27/10), while it is still open.
	 * The wizard shows it on top so the person knows what to change.
	 */
	correctionRequest: z.object({ message: z.string(), requestedAt: z.iso.datetime() }).nullable().optional(),
});

export type OnboardingStateDTO = z.infer<typeof OnboardingStateSchema>;

/** `POST /onboarding/:token/submit` — no body; the server re-checks the minimums. */
export const OnboardingSubmitResponseSchema = z.object({
	status: z.enum(ONBOARDING_STATUSES),
	submittedAt: z.iso.datetime(),
	/**
	 * This submit opened the portal (10/09): a candidate migrated from the
	 * previous software, whose portal was the wizard until now. The end screen
	 * says "you are in" instead of "the team will review".
	 */
	accessUnlocked: z.boolean(),
	/** The same candidate changed imported data: the profile went to review instead (4.1). */
	underReview: z.boolean(),
});

export type OnboardingSubmitResponseDTO = z.infer<typeof OnboardingSubmitResponseSchema>;

/**
 * `POST /onboarding/signup/start` — the public signup begins (23/09).
 *
 * The wizard needs a candidate row to save into, and the public signup used to
 * write nothing until the final click. So the row is born here, at the first
 * file, with only the email — and ONLY with the privacy box ticked: nothing is
 * stored about anybody before that (PDPL), which is why `privacy` is the
 * literal `true` and not a boolean.
 *
 * `website` is a honeypot: hidden on screen, so only a bot fills it in.
 */
export const CandidateSignupStartSchema = z.object({
	email: z.email(),
	privacy: z.literal(true),
	website: z.string().max(200).optional(),
});

export type CandidateSignupStartDTO = z.infer<typeof CandidateSignupStartSchema>;

/**
 * The token of the new draft. Only ever returned for a row this very request
 * created: an address that already has a draft gets its link by email instead
 * (409 `SIGNUP_ALREADY_STARTED`), never in the response.
 */
export const CandidateSignupStartResponseSchema = z.object({
	token: z.string(),
});

export type CandidateSignupStartResponseDTO = z.infer<typeof CandidateSignupStartResponseSchema>;

/**
 * `POST /candidates/:id/onboarding-link` (admin, perm `onboarding:link`) — mint
 * or renew the public link for a candidate. `ttlDays` overrides the default
 * validity; `revokePrevious` kills any live token so only one link works.
 */
export const GenerateOnboardingLinkSchema = z.object({
	ttlDays: z.number().int().min(1).max(365).optional(),
	revokePrevious: z.boolean().optional().default(true),
});

export type GenerateOnboardingLinkDTO = z.infer<typeof GenerateOnboardingLinkSchema>;

export const OnboardingLinkSchema = z.object({
	candidateId: z.number().int(),
	token: z.string(),
	url: z.string(),
	expiresAt: z.iso.datetime(),
});

export type OnboardingLinkDTO = z.infer<typeof OnboardingLinkSchema>;

/**
 * `GET /onboarding/candidates/:id/link` — does this candidate already have a
 * live link, and until when.
 *
 * Deliberately WITHOUT the token: it is the credential of a route that needs no
 * login, so it is returned exactly once, by the endpoint that mints it. The
 * screen needs to know whether a link is outstanding (so it does not silently
 * revoke one already sent by generating another), not what it says.
 */
export const OnboardingLinkStatusSchema = z.object({
	active: z.boolean(),
	expiresAt: z.iso.datetime().nullable(),
});

export type OnboardingLinkStatusDTO = z.infer<typeof OnboardingLinkStatusSchema>;

/** Uploaded-file acknowledgement shared by the photo / CV / video endpoints. */
export const OnboardingUploadResponseSchema = z.object({
	step: z.enum(ONBOARDING_STEPS),
	file: OnboardingFileSchema,
	/**
	 * Solo al subir el CV: id de la lectura con IA que acaba de arrancar
	 * (MD-Onboarding-Candidato-CV-IA §5). El front pregunta por su estado en
	 * `GET .../cv/extraction/:id`. Va el id y no la fila entera para no cruzar
	 * los dos DTO (`cv-extraction.dto` ya depende de este).
	 */
	extractionId: z.number().int().optional(),
});

export type OnboardingUploadResponseDTO = z.infer<typeof OnboardingUploadResponseSchema>;

// ============================================================================
// OpenAPI
// ============================================================================

export const OnboardingStateOpenAPI = createSchema(OnboardingStateSchema).schema;
export const OnboardingLinkOpenAPI = createSchema(OnboardingLinkSchema).schema;
export const PersonalStepOpenAPI = createSchema(PersonalStepSchema).schema;
export const ExperienceStepOpenAPI = createSchema(ExperienceStepSchema).schema;
export const EducationStepOpenAPI = createSchema(EducationStepSchema).schema;
export const LanguagesStepOpenAPI = createSchema(LanguagesStepSchema).schema;
export const LegalStepOpenAPI = createSchema(LegalStepSchema).schema;
export const AvailabilityStepOpenAPI = createSchema(AvailabilityStepSchema).schema;
export const PreferencesStepOpenAPI = createSchema(PreferencesStepSchema).schema;
